import { WooWatermark_isset as isset } from "./Utils";
import { isDevelopmentEnvironment, getImageProxyUrl, logDevelopmentInfo, devLog } from "./DevelopmentConfig";

export default function ImageWithWatermark( image_url='', settings={}, multiple=true ) 
{  
  return new Promise((resolve, reject) => {
    // Log development environment info
    logDevelopmentInfo();
    
    devLog('🔧 ImageWithWatermark called with:');
    devLog('   Image URL:', image_url);
    devLog('   Settings:', settings);
    devLog('   Multiple:', multiple);
    
    const cfg = {};
    cfg.grid_size = settings['grid-size'];
    devLog('📏 Grid size from settings:', cfg.grid_size);
    
    let logo = isset(settings, 'logo', 'url') ? settings['logo'].url : WooWatermark.logo_img;
    devLog('🖼️  Logo URL:', logo);
    
    // Try to get a development-friendly URL for the image
    const processedImageUrl = getImageProxyUrl(image_url);
    if (processedImageUrl !== image_url) {
      devLog(`Using processed image URL: ${processedImageUrl} (original: ${image_url})`);
    }

    // Helper function to load image with CORS handling
    const loadImageWithCORS = (src, callback) => {
      const img = new Image();
      
      // Check if this is a cross-origin request (different port or domain)
      const currentOrigin = window.location.origin;
      const imageUrl = new URL(src, currentOrigin);
      const isCrossOrigin = imageUrl.origin !== currentOrigin || imageUrl.port !== window.location.port;
      
      if (isCrossOrigin) {
        devLog(`Cross-origin image detected: ${src} (from ${imageUrl.origin}:${imageUrl.port} to ${currentOrigin})`);
        
        // For development environments with port mismatches, try multiple strategies
        if (window.location.port && window.location.port !== '80' && window.location.port !== '443') {
          devLog('Development environment detected with port mismatch, trying multiple CORS strategies...');
          
          // Strategy 1: Try with crossOrigin anonymous
          img.crossOrigin = "anonymous";
          
          img.onload = function() {
            callback(null, this);
          };
          
          img.onerror = function() {
            console.warn(`Strategy 1 failed (crossOrigin anonymous), trying without crossOrigin...`);
            
            // Strategy 2: Try without crossOrigin
            img.crossOrigin = null;
            img.src = src;
            
            img.onload = function() {
              callback(null, this);
            };
            
            img.onerror = function() {
              console.warn(`Strategy 2 failed (no crossOrigin), trying with use-credentials...`);
              
              // Strategy 3: Try with use-credentials
              img.crossOrigin = "use-credentials";
              img.src = src;
              
              img.onload = function() {
                callback(null, this);
              };
              
              img.onerror = function() {
                console.error(`All CORS strategies failed for: ${src}`);
                callback(new Error(`All CORS strategies failed for cross-origin image: ${src}`), null);
              };
            };
          };
        } else {
          // Production environment, use standard CORS handling
          img.crossOrigin = "anonymous";
          
          img.onload = function() {
            callback(null, this);
          };
          
          img.onerror = function() {
            console.warn(`CORS failed for cross-origin image: ${src}, trying without crossOrigin`);
            img.crossOrigin = null;
            img.src = src;
            
            img.onload = function() {
              callback(null, this);
            };
            
            img.onerror = function() {
              console.error(`Failed to load cross-origin image: ${src}`);
              callback(new Error(`Failed to load cross-origin image: ${src}`), null);
            };
          };
        }
      } else {
        // Same origin, no CORS needed
        img.onload = function() {
          callback(null, this);
        };
        
        img.onerror = function() {
          console.error(`Failed to load image: ${src}`);
          callback(new Error(`Failed to load image: ${src}`), null);
        };
      }
      
      img.src = src;
    };

    const body = document.getElementsByTagName("body")[0];
    let exists = false;
    let imageCanvas = false;
    let watermarkCanvas = false;

    if( multiple == false ){
      if( document.querySelector( `.${WooWatermark.alias}imageCanvas` ) ){
        exists = true;
      }
    }
    
    // build canvas for the main image and append to the body
    if( exists ) { 
      imageCanvas = document.querySelector( `.${WooWatermark.alias}imageCanvas` );
    }else{
      imageCanvas = document.createElement('canvas');
      imageCanvas.classList.add( `${WooWatermark.alias}imageCanvas` );
      body.appendChild( imageCanvas );
    }

    const imageContext = imageCanvas.getContext("2d");

    // build canvas for the watermark and append to the body
    if( exists ) { 
      watermarkCanvas = document.querySelector( `.${WooWatermark.alias}watermarkCanvas` );
    }else{
      watermarkCanvas = document.createElement('canvas');
      watermarkCanvas.classList.add( `${WooWatermark.alias}watermarkCanvas` );
      body.appendChild( watermarkCanvas );
    }

    const watermarkContext = watermarkCanvas.getContext("2d"); 

    const setImage = ( callback ) => {
      devLog('🖼️  Loading main image:', processedImageUrl);
      
      loadImageWithCORS(processedImageUrl, (error, img) => {
        if (error) {
          console.error('❌ Error loading main image:', error);
          reject(error);
          return;
        }
        
        devLog('✅ Main image loaded successfully:', img.width, 'x', img.height);
        
        cfg.img = {
          'src': image_url, // Keep original URL for reference
          'processed_src': processedImageUrl, // Store processed URL
          'width': img.width,
          'height': img.height,
        };

        if( typeof(callback) == 'function' ){
          callback( img );
        }
      });
    }

    const preloadLogoImage = ( callback ) => {
      devLog('🖼️  Preloading logo image:', logo);
      
      loadImageWithCORS(logo, (error, logoImg) => {
        if (error) {
          console.error('❌ Error loading logo:', error);
          reject(error);
          return;
        }
        
        devLog('✅ Logo loaded successfully:', logoImg.width, 'x', logoImg.height);
        
        cfg.logoImg = {
          'src': logo,
          'width': logoImg.width,
          'height': logoImg.height,
        };

        if( typeof(callback) == 'function' ){
          callback( logoImg );
        }
      });
    }

    const debugWatermark = () => {
      devLog('🔍 Drawing debug watermark border');
      watermarkContext.strokeStyle = "#000";
      watermarkContext.beginPath();
      watermarkContext.rect( 0, 0, cfg.grid_size, cfg.grid_size );
      watermarkContext.stroke();
      devLog('✅ Debug watermark border drawn');
    }

    const drawLines = ( logoImg ) => {
      devLog('📏 Drawing lines on watermark canvas');
      devLog('   Line width:', settings['line-width']);
      devLog('   Line color:', settings['line-color']);
      devLog('   Line opacity:', settings['line-opacity']);
      devLog('   Line blur:', settings['line-blur']);

      watermarkContext.shadowBlur = settings['line-blur'];
      watermarkContext.shadowColor = settings['line-blur-color'];

      // lines 
      const goBackSize = logoImg.height / 2 + 10 /* extra padding */;
      devLog('📏 Go back size for lines:', goBackSize);

      const line_width = settings['line-width'];

      watermarkContext.globalAlpha = settings['line-opacity'];
      watermarkContext.setLineDash([line_width, line_width + 4]);
      watermarkContext.beginPath();
      watermarkContext.strokeStyle = settings['line-color'];

      // line top left
      watermarkContext.moveTo( line_width, line_width );
      watermarkContext.lineTo( cfg.grid_size / 2 - goBackSize, cfg.grid_size / 2 - goBackSize  );

      // line top right
      watermarkContext.moveTo( cfg.grid_size - line_width, line_width );
      watermarkContext.lineTo( cfg.grid_size / 2 + goBackSize, cfg.grid_size / 2 - goBackSize  );

      // line bottom left
      watermarkContext.moveTo( line_width, cfg.grid_size - line_width );
      watermarkContext.lineTo( cfg.grid_size / 2 - goBackSize, cfg.grid_size / 2 + goBackSize  );

      // line bottom right
      watermarkContext.moveTo( cfg.grid_size + line_width, cfg.grid_size + line_width );
      watermarkContext.lineTo( cfg.grid_size / 2 + goBackSize, cfg.grid_size / 2 + goBackSize  );

      watermarkContext.stroke(); 
      devLog('✅ Lines drawn on watermark canvas');
    }

    const drawLogo = ( theLogoImage ) => {
      devLog('🎨 Drawing logo on watermark canvas');
      devLog('   Logo dimensions:', theLogoImage.width, 'x', theLogoImage.height);
      devLog('   Grid size:', cfg.grid_size);
      devLog('   Logo opacity:', settings['logo-opacity']);
      devLog('   Logo blur:', settings['logo-blur']);
      
      watermarkContext.shadowBlur = settings['logo-blur'];
      watermarkContext.shadowColor = settings['logo-blur-color'];

      watermarkContext.globalAlpha = settings['logo-opacity'];

      // Calculate logo position
      const logoX = ( cfg.grid_size - theLogoImage.width ) / 2;
      const logoY = ( cfg.grid_size - theLogoImage.height ) / 2;
      devLog('📍 Logo position on watermark canvas:', logoX, logoY);

      // draw the logo
      watermarkContext.drawImage( 
        theLogoImage, 
        logoX, 
        logoY 
      );
      devLog('✅ Logo drawn on watermark canvas');

      watermarkContext.shadowBlur = settings['line-blur'];
      watermarkContext.shadowColor = settings['line-blur-color'];
    }

    const drawText = () => {
      devLog('📝 Drawing text on watermark canvas');
      devLog('   Custom text:', settings['custom-text']);
      devLog('   Text size:', settings['text-size']);
      devLog('   Text color:', settings['text-color']);
      
      if( !isset(cfg, 'textSize', 'height') ) measureText();

      watermarkContext.font = `${settings['text-size']}px Arial`;
      watermarkContext.textAlign = "center";
      watermarkContext.fillStyle = settings['text-color'];
      
      const textX = cfg.grid_size / 2;
      const textY = cfg.grid_size / 2 + cfg.textSize.height / 2 - 3;
      devLog('📍 Text position on watermark canvas:', textX, textY);
      
      watermarkContext.fillText( settings['custom-text'], textX, textY );
      devLog('✅ Text drawn on watermark canvas');
    }

    const measureText = () => {
      devLog('📏 Measuring text dimensions');
      devLog('   Text to measure:', settings['custom-text']);
      devLog('   Font size:', settings['text-size']);
      
      var d = document.createElement("span");
      d.textContent = settings['custom-text'];
      d.style.cssText = `font-family: Arial !important; color: red !important; font-size: ${settings['text-size']}px !important;`
      document.body.appendChild(d);
      
      cfg.textSize = {
        width: d.offsetWidth,
        height: d.offsetHeight,
      };

      devLog('📏 Measured text dimensions:', cfg.textSize);

      document.body.removeChild(d);
    }

    setImage(( theImage ) => {
      devLog('🖼️  Main image loaded:', theImage.width, 'x', theImage.height);

      // draw main image
      imageCanvas.width = theImage.width;
      imageCanvas.height = theImage.height;
      devLog('🎨 Main canvas created:', imageCanvas.width, 'x', imageCanvas.height);
      
      imageContext.drawImage( theImage, 0, 0 );
      devLog('✅ Main image drawn to canvas');

      // draw watermark context
      watermarkCanvas.width = settings['grid-size'];
      watermarkCanvas.height = settings['grid-size'];
      devLog('🔲 Watermark canvas created:', watermarkCanvas.width, 'x', watermarkCanvas.height);

      
      preloadLogoImage( ( logoImg ) => {

        if( settings['type'] == 'logo' ){
          devLog('🎨 Logo watermark type detected');
          devLog('🖼️  Logo dimensions:', logoImg.width, 'x', logoImg.height);
          
          cfg.grid_size = logoImg.width > logoImg.height ? logoImg.width : logoImg.height;
          devLog('📏 Calculated grid size:', cfg.grid_size);

          watermarkCanvas.width = cfg.grid_size;
          watermarkCanvas.height = cfg.grid_size;
        }

        if( settings['type'] == 'text' ){
          devLog('📝 Text watermark type detected');
          measureText();
          devLog('📏 Text dimensions:', cfg.textSize);

          cfg.grid_size = cfg.textSize.height > cfg.textSize.width ? cfg.textSize.height : cfg.textSize.width;
          devLog('📏 Calculated grid size:', cfg.grid_size);
          
          watermarkCanvas.width = cfg.grid_size;
          watermarkCanvas.height = cfg.grid_size;
        }

        if( settings['type'] == 'logo-stripped-lines' || settings['type'] == 'logo' ){
          drawLogo( logoImg );
        }

        if( settings['type'] == 'text' || settings['type'] == 'text-stripped-lines' ){
          drawText();
        }

        if( settings['type'] == 'text-stripped-lines' || settings['type'] == 'logo-stripped-lines' ){
          devLog('🔲 Stripped lines watermark type detected');
          devLog('📐 Image dimensions:', cfg.img.width, 'x', cfg.img.height);
          devLog('🔲 Grid size:', cfg.grid_size);
          
          drawLines( logoImg );
          devLog('📏 Lines drawn on watermark canvas');

          // draw logo canvas multiple times
          const rows = Math.ceil(cfg.img.height / cfg.grid_size);
          const cols = Math.ceil(cfg.img.width / cfg.grid_size);
          devLog(`🔲 Drawing watermark ${rows} rows x ${cols} columns`);
          
          for (var i = Math.ceil(cfg.img.height / cfg.grid_size) - 1; i >= 0; i--) {
            for (var cc = Math.ceil(cfg.img.width / cfg.grid_size) - 1; cc >= 0; cc--) {
              imageContext.drawImage(watermarkCanvas, cc * cfg.grid_size, i * cfg.grid_size);
            }
          }
          devLog('✅ Stripped lines watermark applied');
        }

        if( settings['type'] == 'text' || settings['type'] == 'logo' ){
          devLog('📍 Single watermark type detected');
          devLog('🎯 Alignment:', settings['align']);
          devLog('📐 Image dimensions:', cfg.img.width, 'x', cfg.img.height);
          devLog('🔲 Watermark size:', cfg.grid_size);

          let xPos = 0;
          let yPos = 0;
          const align = settings['align'];

          if( align == 'top-left' ){
            xPos = settings['margin-left'];
            yPos = settings['margin-top'];
          }
          else if( align == 'top-right' ){
            xPos = cfg.img.width - cfg.grid_size - settings['margin-right'];
            yPos = settings['margin-top'];
          }
          else if( align == 'center' ){
            xPos = cfg.img.width / 2 - cfg.grid_size / 2;
            yPos = cfg.img.height / 2 - cfg.grid_size / 2;
          }
          else if( align == 'bottom-left' ){
            xPos = settings['margin-left'];
            yPos = cfg.img.height - cfg.grid_size - settings['margin-bottom'];
          }
          else if( align == 'bottom-right' ){
            xPos = cfg.img.width - cfg.grid_size - settings['margin-right'];
            yPos = cfg.img.height - cfg.grid_size - settings['margin-bottom'];
          }

          devLog('📍 Watermark position:', xPos, yPos);
          devLog('📏 Margins:', {
            left: settings['margin-left'],
            top: settings['margin-top'],
            right: settings['margin-right'],
            bottom: settings['margin-bottom']
          });

          imageContext.drawImage( watermarkCanvas, xPos, yPos );
          devLog('✅ Single watermark applied at position', xPos, yPos);
        }
        

        // Try to get data URL, if it fails due to CORS, use blob approach
        try {
          // Test if canvas is tainted
          imageCanvas.toDataURL("image/jpeg");
          resolve( imageCanvas );
        } catch (error) {
          // Canvas is tainted, try to convert to blob
          imageCanvas.toBlob((blob) => {
            if (blob) {
              // Create a new canvas from the blob
              const url = URL.createObjectURL(blob);
              const newImg = new Image();
              newImg.onload = function() {
                const newCanvas = document.createElement('canvas');
                newCanvas.width = this.width;
                newCanvas.height = this.height;
                const ctx = newCanvas.getContext('2d');
                ctx.drawImage(this, 0, 0);
                URL.revokeObjectURL(url);
                resolve( newCanvas );
              };
              newImg.src = url;
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/jpeg');
        }
      })
    })
  })
}